import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Users, Camera, Bell, UserPlus, UserCheck, Check, ChevronRight, Share, Settings } from "lucide-react";
import { api, ApiError, normalizeMe, type InboxItem, type Me, type PublicUser, type TodayInfo } from "./api";
import { ActionRow, Avatar, Badge, Button, Group, GroupTitle, Input, LinkButton, Muted, PageTitle, Row, RowInput, Sheet, Skeleton, Spinner, Switch, theme } from "./ui";
import { enableNotifications, isNativeApp, nativeCall, signOut, syncNativeSession, type PushState } from "./native";
import { dayLinkURL, deepLinkHash, parseDeepLink, profileFrameURL, readShellLocation, shareDayURL, tabURL } from "./navigation";

// ---------------------------------------------------------------- data

type Config = { devLogin: boolean; devPassword: boolean; apple: boolean };

function useMe() {
  const [me, setMe] = useState<Me | null | undefined>(undefined); // undefined = loading
  const [config, setConfig] = useState<Config | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const reloadVersion = useRef(0);
  const reload = useCallback(async () => {
    const version = ++reloadVersion.current;
    try {
      const next = normalizeMe(await api<Me>("GET", "me"));
      if (version !== reloadVersion.current) return;
      setMe(next); setConnectionError(false);
    } catch (error) {
      if (version !== reloadVersion.current) return;
      if (error instanceof ApiError && error.status === 401) {
        try {
          const next = await api<Config>("GET", "config");
          if (version !== reloadVersion.current) return;
          setMe(null); setConfig(next); setConnectionError(false);
        } catch { if (version === reloadVersion.current) setConnectionError(true); }
      } else setConnectionError(true); // A network failure must not sign the user out.
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    window.addEventListener("ned:native-change", reload);
    window.addEventListener("online", reload);
    return () => { window.removeEventListener("ned:native-change", reload); window.removeEventListener("online", reload); };
  }, [reload]);
  useEffect(() => {
    if (me !== undefined) syncNativeSession(me).then((unread) => {
      if (typeof unread === "number") setMe((current) => current && current === me && current.unread !== unread ? { ...current, unread } : current);
    }).catch((e) => console.warn("Native session sync:", e));
  }, [me]);
  const apply = useCallback((next: Me) => setMe(normalizeMe(next)), []);
  return { me, config, reload, apply, connectionError };
}

// ---------------------------------------------------------------- login

const LoginForm = styled.form`
  max-width: 360px; margin: 80px auto; padding: 24px; background: ${theme.card}; border-radius: 16px;
  display: flex; flex-direction: column; gap: 10px;
  h1 { margin: 0 0 6px; font-size: 22px; }
`;

function Login({ config, onDone }: { config: Config; onDone: () => void }) {
  const [email, setEmail] = useState(localStorage.getItem("devEmail") || "");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [showDev, setShowDev] = useState(!config.apple);
  const [appleBusy, setAppleBusy] = useState(false);
  const [nativeApple, setNativeApple] = useState(isNativeApp);
  useEffect(() => {
    if (isNativeApp()) nativeCall<{ appleSignIn?: boolean }>("capabilities")
      .then((caps) => setNativeApple(caps.appleSignIn !== false)).catch(console.warn);
  }, []);
  const appleSignIn = async () => {
    setAppleBusy(true); setErr("");
    try {
      const result = await nativeCall<{ ok: boolean; cancelled: boolean }>("appleSignIn");
      if (result.ok) onDone();
    } catch (error) { setErr((error as Error).message); }
    finally { setAppleBusy(false); }
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      localStorage.setItem("devEmail", email);
      await api("POST", "dev-login", { email, firstName: first, lastName: last, password });
      onDone();
    } catch (ex) { setErr((ex as Error).message); }
  };
  return (
    <LoginForm onSubmit={submit}>
      <h1>New Every Day</h1>
      <Muted>A new social network every day.</Muted>
      {config.apple && (nativeApple
        ? <Button type="button" $primary disabled={appleBusy} onClick={appleSignIn}>{appleBusy ? "Signing in…" : "Sign in with Apple"}</Button>
        : <LinkButton $primary href="/api/auth/apple/start" style={{ justifyContent: "center" }}> Sign in with Apple</LinkButton>)}
      {config.devLogin && config.apple && !showDev && <Muted style={{ textAlign: "center" }}><a href="#" onClick={(e) => { e.preventDefault(); setShowDev(true); }} style={{ color: theme.muted }}>developer sign-in</a></Muted>}
      {config.devLogin && showDev && <>
        <Muted>Dev login. Any email works; the name is only used on first sign-in.</Muted>
        <Input placeholder="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input placeholder="first name" value={first} onChange={(e) => setFirst(e.target.value)} />
        <Input placeholder="last name" value={last} onChange={(e) => setLast(e.target.value)} />
        {config.devPassword && <Input placeholder="dev password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        <Button $primary>Sign in</Button>
      </>}
      {err && <Muted role="alert">{err}</Muted>}
    </LoginForm>
  );
}

// ---------------------------------------------------------------- onboarding takeover (username + avatar)

const Takeover = styled.div`
  position: fixed; inset: 0; z-index: 20; background: ${theme.bg}; display: flex; align-items: center; justify-content: center; padding: 20px;
  form { width: 100%; max-width: 380px; background: ${theme.card}; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 12px; }
  h1 { margin: 0; font-size: 22px; }
`;

function AvatarPicker({ me, apply, size = 64 }: { me: Me; apply: (m: Me) => void; size?: number }) {
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const upload = async (f: File) => {
    setBusy(true);
    try {
      const blob = await shrink(f, 512);
      const res = await fetch("/api/avatar", { method: "POST", body: blob, headers: { "content-type": "image/jpeg" } });
      if (!res.ok) throw new Error((await res.json()).error);
      apply(await res.json());
    } catch (e) { alert((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar $size={size} src={me.avatar} onClick={() => input.current?.click()} />
      <Button $small type="button" disabled={busy} onClick={() => input.current?.click()}>{busy ? <Spinner $small /> : <><Camera size={15} /> Choose photo</>}</Button>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
    </div>
  );
}

async function shrink(file: File, maxSide: number): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  return new Promise((r) => c.toBlob((b) => r(b!), "image/jpeg", 0.85));
}

/** Checks availability as you type; reports validity up. Renders just the field + message (no chrome) so it fits a Row or a form. */
function useUsernameCheck(me: Me, value: string, onValid: (ok: boolean) => void): string {
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!value) { setMsg(""); onValid(false); return; }
    if (value === me.username) { setMsg(""); onValid(true); return; }
    const t = setTimeout(async () => {
      const r = await api<{ ok: boolean; reason?: string; username?: string }>("GET", `username/check?u=${encodeURIComponent(value)}`);
      setMsg(r.ok ? `@${r.username} is available` : r.reason || "taken");
      onValid(r.ok);
    }, 300);
    return () => clearTimeout(t);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return msg;
}

function Onboarding({ me, apply }: { me: Me; apply: (m: Me) => void }) {
  const [username, setUsername] = useState(`${me.firstName}${me.lastName}`.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20));
  const [valid, setValid] = useState(false);
  const [err, setErr] = useState("");
  const msg = useUsernameCheck(me, username, setValid);
  return (
    <Takeover>
      <form onSubmit={async (e) => { e.preventDefault(); try { apply(await api<Me>("POST", "username", { username })); } catch (ex) { setErr((ex as Error).message); } }}>
        <h1>Pick a username</h1>
        <Muted>Friends find you by it. You can change it later in Profile.</Muted>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: theme.muted }}>@</span><Input style={{ flex: 1 }} placeholder="username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} autoCapitalize="off" /></div>
        <Muted>{msg || "3–20 letters, numbers or underscores"}</Muted>
        <Muted>Add a photo (optional)</Muted>
        <AvatarPicker me={me} apply={apply} />
        {err && <Muted style={{ color: theme.danger }}>{err}</Muted>}
        <Button $primary disabled={!valid}>Continue</Button>
      </form>
    </Takeover>
  );
}

// ---------------------------------------------------------------- shell + tab bar

const Root = styled.div`height: 100%; display: flex; flex-direction: column;`;
const Content = styled.div`flex: 1; min-height: 0; display: flex; justify-content: center; position: relative;`;
const FrameHost = styled.div<{ $active: boolean }>`
  position: absolute; inset: 0; display: ${(p) => (p.$active ? "flex" : "none")}; justify-content: center;
  iframe { width: 100%; max-width: 480px; height: 100%; border: 0; background: ${theme.card}; }
`;
/** Full-width scroll view; only the content column is capped. */
const Pane = styled.div`
  width: 100%; height: 100%; overflow: auto; background: ${theme.bg}; padding: 10px 12px calc(20px + max(env(safe-area-inset-bottom), var(--native-bottom, 0px)));
  > .inner { max-width: 480px; margin: 0 auto; }
`;
const TabBar = styled.div`
  display: flex; justify-content: center; border-top: 1px solid ${theme.line}; background: ${theme.card}; padding-bottom: env(safe-area-inset-bottom);
  .inner { display: flex; width: 100%; max-width: 480px; }
  button { flex: 1; border: 0; background: none; padding: 8px 4px 6px; display: flex; flex-direction: column; align-items: center; gap: 2px; color: ${theme.muted}; font-size: 11px; cursor: pointer; position: relative; -webkit-user-select: none; user-select: none; }
  button.on { color: ${theme.fg}; font-weight: 600; }
  .ico { font-size: 22px; line-height: 1.1; height: 26px; display: flex; align-items: center; }
  .badge { position: absolute; top: 4px; right: calc(50% - 22px); }
`;

/** `fromSite`: the site asked for it (social.showProfile), so don't offer to open the site's own profile page. */
type SheetState = { kind: "profile"; userId: string; fromSite?: boolean } | null;

const DayBanner = styled.div`
  position: absolute; top: 8px; left: 50%; transform: translateX(-50%); z-index: 3; display: flex; gap: 8px; align-items: center;
  background: ${theme.fg}; color: #fff; padding: 6px 10px 6px 14px; border-radius: 999px; font-size: 13px; box-shadow: 0 4px 16px rgba(0,0,0,.25);
  button { background: #fff; color: ${theme.fg}; border: 0; border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 600; cursor: pointer; }
`;

const ProfileRoot = styled.div`
  position: absolute; inset: 0; display: flex; flex-direction: column;
  > header { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background: ${theme.card}; border-bottom: 1px solid ${theme.line}; }
  h1 { margin: 0; font-size: 18px; }
  > .body { flex: 1; min-height: 0; position: relative; }
`;

function Shell({ me, apply, reload }: { me: Me; apply: (m: Me) => void; reload: () => void }) {
  const [sheet, setSheet] = useState<SheetState>(null);
  const [target, setTarget] = useState(() => readShellLocation(location.href).target);
  const [info, setInfo] = useState<TodayInfo | null>(null);
  const [frameKey, setFrameKey] = useState(0);
  const [tab, setTab] = useState(() => readShellLocation(location.href).tab);
  const [hash, setHash] = useState(location.hash);
  const [siteError, setSiteError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [profileSettings, setProfileSettings] = useState(false);
  const frames = useRef(new Map<string, HTMLIFrameElement>());
  const pane = useRef<HTMLDivElement>(null);
  // On iOS the page runs under the native tab bar. env(safe-area-inset-*) reads 0 there, so the app pushes the
  // overlap as window.nedInsets (+ a "ned:insets" event); tell each site how much of its bottom is covered.
  const sendInsets = useCallback((frame?: HTMLIFrameElement) => {
    const bottom = (window as unknown as { nedInsets?: { bottom?: number } }).nedInsets?.bottom || 0;
    document.documentElement.style.setProperty("--native-bottom", `${bottom}px`);
    const msg = { type: "social:insets", bottom };
    for (const f of frame ? [frame] : frames.current.values()) f.contentWindow?.postMessage(msg, me.sandboxOrigin);
  }, [me.sandboxOrigin]);
  useEffect(() => {
    sendInsets();
    const onInsets = () => sendInsets();
    window.addEventListener("ned:insets", onInsets);
    return () => window.removeEventListener("ned:insets", onInsets);
  }, [sendInsets]);
  /**
   * Bumped on every explicit selection (a tap, a navigation, social.switchTab) — not when the shell merely restates
   * its tab list — so the iOS bar knows to move even when it's showing a native tab the web shell never heard about.
   */
  const selectSeq = useRef(0);
  /** A deep link's meta.json tab ("#~feed/…"), selected once the site's tabs are known. */
  const pendingLinkTab = useRef(tab.startsWith("site:") ? parseDeepLink(location.hash).tab : null);
  const readNavigation = useCallback(() => {
    const next = readShellLocation(location.href);
    selectSeq.current++;
    pendingLinkTab.current = next.tab.startsWith("site:") ? parseDeepLink(next.hash).tab : null;
    setTarget(next.target); setTab(next.tab); setHash(next.hash); setSheet(null);
  }, []);
  useEffect(() => {
    window.addEventListener("popstate", readNavigation);
    window.addEventListener("hashchange", readNavigation);
    return () => { window.removeEventListener("popstate", readNavigation); window.removeEventListener("hashchange", readNavigation); };
  }, [readNavigation]);
  /** Tell a site frame to re-sync (live subs reconnect; the site's useRefresh handlers run). */
  const refreshFrame = useCallback((id: string) => {
    frames.current.get(id)?.contentWindow?.postMessage({ type: "social:refresh" }, me.sandboxOrigin);
  }, [me.sandboxOrigin]);
  const selectTab = useCallback((id: string) => {
    history.replaceState(null, "", tabURL(location.href, id));
    selectSeq.current++;
    setTab(id);
    refreshFrame(id);
  }, [refreshFrame]);
  // Pull-to-refresh / foreground in the iOS app (ned:refresh), or the page becoming visible on the web.
  const tabRef = useRef(tab); tabRef.current = tab;
  useEffect(() => {
    const onRefresh = () => { refreshFrame(tabRef.current); reload(); };
    const onVisible = () => { if (document.visibilityState === "visible") refreshFrame(tabRef.current); };
    window.addEventListener("ned:refresh", onRefresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("ned:refresh", onRefresh); document.removeEventListener("visibilitychange", onVisible); };
  }, [refreshFrame, reload]);
  // Blocking or unblocking someone changes what every site may show; make the open one re-sync.
  const blockedKey = me.blocked.join(",");
  const firstBlocked = useRef(blockedKey);
  useEffect(() => { if (blockedKey !== firstBlocked.current) { firstBlocked.current = blockedKey; refreshFrame(tabRef.current); } }, [blockedKey, refreshFrame]);
  // Same for following someone from Friends/Profile (web or native): their posts should appear without a reload.
  const followingKey = me.following.join(",");
  const firstFollowing = useRef(followingKey);
  useEffect(() => { if (followingKey !== firstFollowing.current) { firstFollowing.current = followingKey; refreshFrame(tabRef.current); } }, [followingKey, refreshFrame]);
  const navigate = (href: string) => { history.pushState(null, "", href); readNavigation(); };
  // The iOS app opens notifications and universal links in place (ned:open) instead of reloading the document,
  // so the native tab bar never tears down and rebuilds.
  useEffect(() => {
    const onOpen = (event: Event) => {
      const href = (event as CustomEvent<{ href?: string }>).detail?.href;
      if (typeof href !== "string" || !href.startsWith("/")) return;
      navigate(href);
      setFrameKey((k) => k + 1);
    };
    window.addEventListener("ned:open", onOpen);
    return () => window.removeEventListener("ned:open", onOpen);
  }); // eslint-disable-line react-hooks/exhaustive-deps -- navigate is recreated each render
  const retrySite = () => { setRetryCount((n) => n + 1); setFrameKey((n) => n + 1); reload(); };
  const reloadFrame = useCallback(() => setFrameKey((k) => k + 1), []);
  // Resolve the site behind the current view, and keep watching it: when its files change
  // (an admin edit, a reassignment), remount the frames so everyone sees the new version.
  useEffect(() => {
    let last: string | null = null;
    let cancelled = false;
    let pending = false;
    setInfo(null);
    const load = async () => {
      if (pending) return;
      pending = true;
      try {
        let t: TodayInfo;
        if (target.siteId) {
          const s = await api<{ meta: TodayInfo["meta"]; title?: string; name: string; updatedAt: number }>("GET", `admin/sites/${encodeURIComponent(target.siteId)}`);
          t = { day: target.seg, siteId: target.siteId, title: s.title || s.name, meta: s.meta || null, updatedAt: s.updatedAt, today: me.today };
        } else t = await api<TodayInfo>("GET", `today${target.isToday ? "" : `?day=${encodeURIComponent(target.seg)}`}`);
        if (cancelled) return;
        const sig = `${t.day}:${t.siteId}:${t.updatedAt}`;
        if (last !== null && sig !== last) setFrameKey((k) => k + 1);
        last = sig;
        setInfo(t); setSiteError(false);
      } catch { if (!cancelled) setSiteError(true); }
      finally { pending = false; }
    };
    load();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") load(); }, 10000);
    window.addEventListener("online", load);
    window.addEventListener("ned:native-change", load);
    return () => {
      cancelled = true; clearInterval(timer);
      window.removeEventListener("online", load); window.removeEventListener("ned:native-change", load);
    };
  }, [target, me.today, retryCount]);
  const goToday = () => navigate("/");

  const siteTabs = info?.meta?.tabs?.length ? info.meta.tabs : [{ id: "", title: info?.meta?.name || "Today", emoji: info?.meta?.icon || "✨" }];
  const friendsBadge = me.unread + me.requests.length;
  // A deep link only goes to the tab it names (or the first tab); the other tabs open at their roots.
  const link = parseDeepLink(hash);
  const linkIndex = Math.max(0, link.tab ? siteTabs.findIndex((t) => t.id === link.tab) : 0);
  useEffect(() => {
    if (!info || !pendingLinkTab.current) return;
    const i = (info.meta?.tabs || []).findIndex((t) => t.id === pendingLinkTab.current);
    pendingLinkTab.current = null;
    if (i >= 0) selectTab(`site:${i}`);
  }, [info, selectTab]);
  // meta.json `profile` names the site's own profile route; Friends/Profile sheets offer to open it.
  const profile = info?.meta?.profile;
  const ownProfile = profile && { ...profile, tab: profile.tab || siteTabs[0].id || undefined };
  const profileIdentity = JSON.stringify([me.firstName, me.lastName, me.username, me.bio, me.avatar, me.isPrivate]);
  const profileHref = (userId: string) => profile ? dayLinkURL(location.href, deepLinkHash(profile.tab, profile.route.replace("{id}", encodeURIComponent(userId)))) : null;
  const openLink = (href: string) => { navigate(href); reloadFrame(); };
  useEffect(() => {
    const tabs = [
      ...siteTabs.map((t, i) => ({ id: `site:${i}`, title: t.title, symbol: "symbol" in t ? t.symbol : "sparkles", badge: 0 })),
      { id: "friends", title: "Friends", symbol: "person.2", badge: friendsBadge },
      { id: "profile", title: "Profile", symbol: "person.crop.circle", badge: 0 },
    ];
    const selected = tabs.some((t) => t.id === tab) ? tab : "site:0";
    if (info && selected !== tab) selectTab(selected);
    if (!isNativeApp()) return;
    // Profile hosts the day's web profile, so its chrome follows the day too.
    const onSite = selected.startsWith("site:") || selected === "profile" && !!profile;
    nativeCall("tabs", {
      tabs, selected: info ? selected : tab, selectSeq: selectSeq.current, loading: !info && !siteError, shareURL: info?.siteId ? shareDayURL(location.href, info.day) : null,
      // Native Friends/Profile sheets substitute the user id for "{id}".
      profileLink: profile && info?.meta ? { title: info.meta.name, href: location.origin + dayLinkURL(location.href, deepLinkHash(profile.tab, profile.route)) } : null,
      colorScheme: onSite ? info?.meta?.colorScheme || null : "light",
      background: onSite ? info?.meta?.background || "#ffffff" : theme.bg,
      backgroundDark: onSite ? info?.meta?.backgroundDark || null : null,
    }).catch(console.warn);
    const select = (event: Event) => {
      const id = (event as CustomEvent<{ id: string }>).detail?.id;
      if (tabs.some((t) => t.id === id)) {
        if (id === tab) frames.current.get(id)?.contentWindow?.postMessage({ type: "social:home" }, me.sandboxOrigin);
        selectTab(id);
      }
    };
    window.addEventListener("ned:select-tab", select);
    return () => window.removeEventListener("ned:select-tab", select);
  }, [info, siteError, tab, friendsBadge, hash, selectTab]);
  // The site's declared scheme drives the document's color-scheme (form controls, scrollbars) while a site tab is showing.
  const colorScheme = info?.meta?.colorScheme || "light";
  useEffect(() => {
    document.documentElement.style.colorScheme = tab.startsWith("site:") || tab === "profile" && !!profile ? (colorScheme === "auto" ? "light dark" : colorScheme) : "light";
  }, [tab, colorScheme, profile]);
  const frameSrc = (t: { id: string }, i: number) => `${me.sandboxOrigin}/sandbox/${target.seg}/?${t.id ? `tab=${t.id}&` : ""}v=${info?.updatedAt || 0}${profile ? `&profile=${encodeURIComponent(profile.route.replace("{id}", encodeURIComponent(me.id)))}` : ""}${i === linkIndex && link.route ? `#${link.route}` : ""}`;

  // Messages from the site: open a system profile, or switch to one of its tabs.
  const tabsRef = useRef(siteTabs); tabsRef.current = siteTabs;
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== me.sandboxOrigin) return;
      const d = ev.data as { type?: string; userId?: string; tabId?: string; route?: string } | null;
      if (d?.type === "social:showProfile" && d.userId) {
        if (d.userId === me.id) {
          setSheet(null); setProfileSettings(false);
          frames.current.get("profile")?.contentWindow?.postMessage({ type: "social:home" }, me.sandboxOrigin);
          selectTab("profile");
        }
        else setSheet({ kind: "profile", userId: d.userId, fromSite: true });
      }
      if (d?.type === "social:switchTab" && typeof d.tabId === "string") {
        const i = tabsRef.current.findIndex((t) => t.id === d.tabId);
        if (i < 0) return;
        selectTab(`site:${i}`);
        if (typeof d.route === "string") frames.current.get(`site:${i}`)?.contentWindow?.postMessage({ type: "social:route", route: d.route }, me.sandboxOrigin);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [me.sandboxOrigin, me.id, selectTab]);

  /** Tapping the tab you're already on returns it to its root. */
  const pick = (next: string) => {
    if (next !== tab) { selectTab(next); return; }
    if (next === "profile") setProfileSettings(false);
    if (frames.current.has(next)) frames.current.get(next)?.contentWindow?.postMessage({ type: "social:home" }, me.sandboxOrigin);
    else pane.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Root>
      {siteError && <RetryNotice message="Couldn't update this day. Check your connection and try again." onRetry={retrySite} />}
      <Content>
        {siteTabs.map((t, i) => (
          <FrameHost key={`${target.seg}:${t.id}:${frameKey}`} $active={tab === `site:${i}`}>
            <iframe ref={(el) => { if (el) frames.current.set(`site:${i}`, el); else frames.current.delete(`site:${i}`); }} onLoad={(e) => sendInsets(e.currentTarget)} src={frameSrc(t, i)} title={t.title} sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads" allow="camera; microphone" />
          </FrameHost>
        ))}
        {tab === "friends" && <Pane ref={pane}><div className="inner"><Friends me={me} apply={apply} onProfile={(id) => setSheet({ kind: "profile", userId: id })}
          onOpenNotif={(item) => { const day = item.day && item.day !== me.today ? `?day=${item.day}` : ""; navigate(`/${day}${item.url && item.url.startsWith("#") ? item.url : ""}`); reloadFrame(); }} /></div></Pane>}
        <ProfileRoot style={{ display: tab === "profile" ? "flex" : "none" }}>
          {!isNativeApp() && <header><h1>Profile</h1><Button $small aria-label="Edit profile and settings" onClick={() => setProfileSettings(true)}><Settings size={20} /></Button></header>}
          <div className="body">
            {ownProfile && info ? <FrameHost key={`${target.seg}:profile:${frameKey}:${profileIdentity}`} $active>
              <iframe ref={(el) => { if (el) frames.current.set("profile", el); else frames.current.delete("profile"); }} onLoad={(e) => sendInsets(e.currentTarget)}
                src={profileFrameURL(me.sandboxOrigin, target.seg, ownProfile, me.id, info.updatedAt || 0)} title="Your profile"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads" allow="camera; microphone" />
            </FrameHost> : <Pane><div className="inner">{!info && !siteError ? <Skeleton /> : <>
              <PageTitle>{me.firstName} {me.lastName}</PageTitle><Avatar $size={64} src={me.avatar} /><p>@{me.username}</p><p>{me.bio}</p>
              <Muted>{info?.siteId ? "This day hasn't provided a profile view yet." : "Your profile for the day will appear here."}</Muted>
            </>}</div></Pane>}
          </div>
        </ProfileRoot>
        {!target.isToday && tab.startsWith("site:") && <DayBanner>{info?.meta?.name || target.seg}<button onClick={goToday}>Back to today</button></DayBanner>}
      </Content>
      {!isNativeApp() && <TabBar>
        <div className="inner">
          {siteTabs.map((t, i) => (
            <button key={i} className={tab === `site:${i}` ? "on" : ""} onClick={() => pick(`site:${i}`)}><span className="ico">{t.emoji || info?.meta?.icon || "✨"}</span>{t.title}</button>
          ))}
          <button className={tab === "friends" ? "on" : ""} onClick={() => pick("friends")}><span className="ico"><Users size={24} strokeWidth={tab === "friends" ? 2.4 : 1.8} /></span>Friends{friendsBadge > 0 && <Badge className="badge">{friendsBadge}</Badge>}</button>
          <button className={tab === "profile" ? "on" : ""} onClick={() => pick("profile")}><span className="ico"><Avatar $size={24} src={me.avatar} style={{ outline: tab === "profile" ? `2px solid ${theme.fg}` : "none" }} /></span>Profile</button>
        </div>
      </TabBar>}
      {sheet?.kind === "profile" && <ProfileSheet me={me} apply={apply} userId={sheet.userId} onClose={() => setSheet(null)}
        siteLink={!sheet.fromSite && info?.meta?.profile ? { title: info.meta.name, href: profileHref(sheet.userId)! } : null} onOpenLink={openLink} />}
      {profileSettings && <Sheet title="Profile settings" onClose={() => setProfileSettings(false)}><ProfileSettings me={me} apply={apply} reload={reload} /><Button onClick={() => setProfileSettings(false)}>Done</Button></Sheet>}
    </Root>
  );
}

// ---------------------------------------------------------------- people

function FollowButton({ me, apply, user }: { me: Me; apply: (m: Me) => void; user: PublicUser }) {
  const following = me.following.includes(user.id);
  const requested = me.requested.includes(user.id);
  const label = following ? "Following" : requested ? "Requested" : user.isPrivate ? "Request" : "Follow";
  return (
    <Button $small $primary={!following && !requested}
      onClick={async (e) => { e.stopPropagation(); apply(await api<Me>("POST", following || requested ? "unfollow" : "follow", { userId: user.id })); }}>
      {label}
    </Button>
  );
}

function PersonRow({ me, apply, user, onProfile }: { me: Me; apply: (m: Me) => void; user: PublicUser; onProfile?: (id: string) => void }) {
  const isMe = user.id === me.id;
  const sub = [user.username ? `@${user.username}` : user.bio || "", me.followers.includes(user.id) ? "follows you" : user.isPrivate ? "private" : ""].filter(Boolean).join(" · ");
  return (
    <Row $tappable={!!onProfile} onClick={() => onProfile?.(user.id)}>
      <Avatar src={user.avatar} />
      <div className="grow">
        <div className="name">{user.firstName} {user.lastName}{user.isPrivate ? " 🔒" : ""}</div>
        <div className="sub">{sub}</div>
      </div>
      {isMe ? <Muted>you</Muted> : <FollowButton me={me} apply={apply} user={user} />}
    </Row>
  );
}

function RequestRow({ user, apply, onProfile }: { user: PublicUser; apply: (m: Me) => void; onProfile: (id: string) => void }) {
  const act = (path: string) => async (e: React.MouseEvent) => { e.stopPropagation(); apply(await api<Me>("POST", path, { userId: user.id })); };
  return (
    <Row $tappable onClick={() => onProfile(user.id)}>
      <Avatar src={user.avatar} />
      <div className="grow"><div className="name">{user.firstName} {user.lastName}</div><div className="sub">{user.username ? `@${user.username} · ` : ""}wants to follow you</div></div>
      <Button $small $primary onClick={act("requests/approve")}><Check size={14} /> Approve</Button>
      <Button $small onClick={act("requests/decline")}>Decline</Button>
    </Row>
  );
}

const SearchRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 0 12px; margin: 0 0 4px; background: ${theme.card}; border-radius: 12px;
  input { flex: 1; border: 0; background: transparent; padding: 11px 0; outline: none; font-size: 16px; }
`;

const APP_INVITE_URL = "https://testflight.apple.com/join/45QZThug";

async function shareApp() {
  const data = { title: "New Every Day", text: "Join me on New Every Day", url: APP_INVITE_URL };
  try {
    if (navigator.share) { await navigator.share(data); return; }
    await navigator.clipboard.writeText(APP_INVITE_URL);
    alert("Link copied");
  } catch (e) {
    if ((e as Error).name !== "AbortError") prompt("Copy this link", APP_INVITE_URL);
  }
}

const ShareAppButton = styled.button`
  border: 0; background: transparent; color: ${theme.blue}; padding: 6px; display: flex; cursor: pointer;
`;

function Friends({ me, apply, onProfile, onOpenNotif }: { me: Me; apply: (m: Me) => void; onProfile: (id: string) => void; onOpenNotif: (i: InboxItem) => void }) {
  const [q, setQ] = useState("");
  const [inbox, setInbox] = useState<InboxItem[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api<{ items: InboxItem[] }>("GET", "inbox").then(async (r) => {
      if (cancelled) return;
      setInbox(r.items);
      if (r.items.some((i) => !i.readAt)) {
        const result = await api<{ unread: number }>("POST", "inbox/read", { ids: r.items.filter((i) => !i.readAt).map((i) => i.id) });
        if (!cancelled) apply({ ...me, unread: result.unread });
      }
    }).catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps -- inbox loads on entry or explicit retry
  const [results, setResults] = useState<PublicUser[] | null>(null);
  const [all, setAll] = useState<PublicUser[] | null>(null); // null until loaded: never show a count of 0 that isn't real
  useEffect(() => {
    let cancelled = false;
    api<PublicUser[]>("GET", "users").then((users) => { if (!cancelled) setAll(users); }).catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [me.following.length, me.requests.length, retryCount]);
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setResults(null);
    let cancelled = false;
    const t = setTimeout(() => api<PublicUser[]>("GET", `users?q=${encodeURIComponent(q.trim())}`)
      .then((users) => { if (!cancelled) setResults(users); }).catch(() => { if (!cancelled) setLoadError(true); }), 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, retryCount]);
  const byId = new Map((all || []).map((u) => [u.id, u]));
  const requests = me.requests.map((id) => byId.get(id)).filter((u): u is PublicUser => !!u);
  const following = (all || []).filter((u) => me.following.includes(u.id));
  const followers = (all || []).filter((u) => me.followers.includes(u.id) && !me.following.includes(u.id));
  const person = (u: PublicUser) => <PersonRow key={u.id} me={me} apply={apply} user={u} onProfile={onProfile} />;
  return (
    <div>
      <PageTitle>Friends<span className="spacer" /><ShareAppButton aria-label="Share app" title="Share app" onClick={shareApp}><Share size={22} /></ShareAppButton></PageTitle>
      {loadError && <RetryNotice message="Couldn't load friends and notifications." onRetry={() => { setLoadError(false); setRetryCount((n) => n + 1); }} />}
      <SearchRow><Users size={18} color={theme.muted} /><input placeholder="Search by name or @username" value={q} onChange={(e) => setQ(e.target.value)} /></SearchRow>
      {q.trim() ? (
        <>
          <GroupTitle>Results</GroupTitle>
          <Group>
            {results === null ? <Skeleton rows={2} /> : results.length === 0 ? <Row><Muted>No one found.</Muted></Row> : results.map(person)}
          </Group>
        </>
      ) : (
        <>
          {inbox && inbox.length > 0 && <>
            <GroupTitle><Bell size={13} /> Notifications</GroupTitle>
            <Group>
              {(showAll ? inbox : inbox.slice(0, 3)).map((i) => <InboxRowView key={i.id} item={i} me={me} onOpen={onOpenNotif} onProfile={onProfile} />)}
              {inbox.length > 3 && !showAll && <ActionRow $center onClick={() => setShowAll(true)}>Show all {inbox.length}</ActionRow>}
            </Group>
          </>}
          {requests.length > 0 && <><GroupTitle><UserPlus size={13} /> Follow requests</GroupTitle><Group>{requests.map((u) => <RequestRow key={u.id} user={u} apply={apply} onProfile={onProfile} />)}</Group></>}
          <GroupTitle><UserCheck size={13} /> Following{all && ` · ${following.length}`}</GroupTitle>
          <Group>
            {all === null ? <Skeleton rows={4} />
              : following.length === 0 ? <Row><Muted>You're not following anyone yet. Search above.</Muted></Row>
              : following.map(person)}
          </Group>
          {all && followers.length > 0 && <><GroupTitle>Follows you</GroupTitle><Group>{followers.map(person)}</Group></>}
        </>
      )}
    </div>
  );
}

function ProfileSettings({ me, apply, reload }: { me: Me; apply: (m: Me) => void; reload: () => void }) {
  const [first, setFirst] = useState(me.firstName);
  const [last, setLast] = useState(me.lastName);
  const [bio, setBio] = useState(me.bio);
  const [username, setUsername] = useState(me.username || "");
  const [valid, setValid] = useState(true);
  const [saved, setSaved] = useState("");
  const msg = useUsernameCheck(me, username, setValid);
  const [pushStatus, setPushStatus] = useState("");
  const [pushDenied, setPushDenied] = useState(false);
  const [settingsSupported, setSettingsSupported] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const refreshPushState = useCallback(async () => {
    if (!isNativeApp()) return;
    try {
      const state = await nativeCall<PushState>("pushState");
      setPushDenied(!!state.denied);
      const capabilities = await nativeCall<{ notificationSettings?: boolean; push?: boolean }>("capabilities");
      setSettingsSupported(!!capabilities.notificationSettings);
      setPushSupported(capabilities.push !== false);
    } catch (error) { console.warn(error); }
  }, []);
  useEffect(() => {
    refreshPushState();
    window.addEventListener("ned:native-change", refreshPushState);
    return () => window.removeEventListener("ned:native-change", refreshPushState);
  }, [refreshPushState]);
  const [blockedUsers, setBlockedUsers] = useState<PublicUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    Promise.all(me.blocked.map((id) => api<PublicUser>("GET", `users/${id}`).catch(() => null)))
      .then((us) => { if (!cancelled) setBlockedUsers(us.filter((u): u is PublicUser => !!u)); });
    return () => { cancelled = true; };
  }, [me.blocked.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const deleteAccount = async () => {
    if (!confirm("Delete your account? Your profile, posts, follows and everything you've made in New Every Day will be permanently deleted. This can't be undone.")) return;
    try {
      await api("DELETE", "me");
      reload();
    } catch (e) { alert((e as Error).message); }
  };
  const dirty = first !== me.firstName || last !== me.lastName || bio !== me.bio || username !== (me.username || "");
  const save = async () => {
    let m = await api<Me>("PATCH", "me", { firstName: first, lastName: last, bio });
    if (username !== (me.username || "")) m = await api<Me>("POST", "username", { username }).catch((e) => { alert(e.message); return m; });
    apply(m); setSaved("Saved"); setTimeout(() => setSaved(""), 1500);
  };
  return (
    <div>
      <PageTitle>Edit profile<span className="spacer" />{saved ? <Muted>{saved}</Muted> : dirty && <Button $small $primary disabled={!valid || !username} onClick={save}>Save</Button>}</PageTitle>
      <Group>
        <Row style={{ padding: "14px 16px 14px 0" }}><AvatarPicker me={me} apply={apply} /></Row>
        <Row><span className="label">First</span><RowInput placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} /></Row>
        <Row><span className="label">Last</span><RowInput placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} /></Row>
        <Row><span className="label" style={{ color: theme.muted }}>@</span><RowInput placeholder="username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} autoCapitalize="off" /><Muted>{msg}</Muted></Row>
        <Row><span className="label">Bio</span><RowInput placeholder="Say something about yourself" value={bio} onChange={(e) => setBio(e.target.value)} /></Row>
      </Group>
      <GroupTitle>Privacy</GroupTitle>
      <Group>
        <Row>
          <div className="grow"><div className="name">Private account</div><div className="sub" style={{ whiteSpace: "normal" }}>New followers must be approved before they can see your posts.</div></div>
          <Switch checked={me.isPrivate} onChange={async (v) => apply(await api<Me>("PATCH", "me", { isPrivate: v }))} />
        </Row>
      </Group>
      {isNativeApp() && <>
        <GroupTitle>Notifications</GroupTitle>
        <Group>
          {pushSupported ? <ActionRow onClick={async () => {
            try {
              if (pushDenied && settingsSupported) await nativeCall("openNotificationSettings");
              else setPushStatus(await enableNotifications(me));
              await refreshPushState();
            } catch (e) { setPushStatus((e as Error).message); }
          }}>{pushDenied && settingsSupported ? "Open notification settings" : "Enable notifications"}</ActionRow>
            : <Row><Muted>Push notifications are available in the production-signed app.</Muted></Row>}
          {pushStatus && <Row><Muted>{pushStatus}</Muted></Row>}
        </Group>
      </>}
      <GroupTitle>Network</GroupTitle>
      <Group>
        <Row><span className="grow">Followers</span><span className="value">{me.followers.length}</span></Row>
        <Row><span className="grow">Following</span><span className="value">{me.following.length}</span></Row>
      </Group>
      {blockedUsers.length > 0 && <>
        <GroupTitle>Blocked</GroupTitle>
        <Group>{blockedUsers.map((u) => <BlockedRow key={u.id} user={u} onUnblock={async () => apply(await api<Me>("POST", "unblock", { userId: u.id }))} />)}</Group>
      </>}
      <GroupTitle>Invite friends</GroupTitle>
      <Group>
        <ActionRow onClick={shareApp}><Share size={17} /> Share app</ActionRow>
      </Group>
      <GroupTitle>Account</GroupTitle>
      <Group>
        {me.admin && <ActionRow as="a" href="/admin">Admin<ChevronRight size={16} style={{ marginLeft: "auto", color: theme.muted }} /></ActionRow>}
        <ActionRow $danger onClick={async () => { try { await signOut(); reload(); } catch (e) { alert((e as Error).message); } }}>Sign out</ActionRow>
        <ActionRow $danger onClick={deleteAccount}>Delete account</ActionRow>
      </Group>
    </div>
  );
}

function ProfileSheet({ me, apply, userId, onClose, siteLink, onOpenLink }: { me: Me; apply: (m: Me) => void; userId: string; onClose: () => void; siteLink: { title: string; href: string } | null; onOpenLink: (href: string) => void }) {
  const [user, setUser] = useState<(PublicUser & { followerCount: number; followingCount: number }) | null>(null);
  const [gone, setGone] = useState(false);
  useEffect(() => { api<PublicUser & { followerCount: number; followingCount: number }>("GET", `users/${userId}`).then(setUser).catch(() => setGone(true)); }, [userId]);
  const blocked = me.blocked.includes(userId);
  const toggleBlock = async () => {
    if (!user) return;
    if (!blocked && !confirm(`Block ${user.firstName}? They won't be able to find you, follow you or see what you post, and you won't see theirs.`)) return;
    apply(await api<Me>("POST", blocked ? "unblock" : "block", { userId }));
  };
  return (
    <Sheet title={user ? `${user.firstName} ${user.lastName}` : " "} onClose={onClose}>
      <Group>
        {gone ? <Row><Muted>This account isn't available.</Muted></Row>
          : user ? (blocked ? <BlockedRow user={user} /> : <PersonRow me={me} apply={apply} user={userId === me.id ? { ...user, isPrivate: me.isPrivate } : user} />) : <Skeleton rows={1} />}
        {user?.bio && !blocked && <Row><span style={{ padding: "4px 0" }}>{user.bio}</span></Row>}
      </Group>
      {user && !blocked && siteLink && <Group style={{ marginTop: 20 }}>
        <ActionRow onClick={() => onOpenLink(siteLink.href)}>Open in {siteLink.title}<ChevronRight size={16} style={{ marginLeft: "auto", color: theme.muted }} /></ActionRow>
      </Group>}
      {user && !blocked && <><GroupTitle>Network</GroupTitle><Group>
        <Row><span className="grow">Followers</span><span className="value">{user.followerCount}</span></Row>
        <Row><span className="grow">Following</span><span className="value">{user.followingCount}</span></Row>
      </Group></>}
      {user && userId !== me.id && <Group style={{ marginTop: 20 }}>
        <ActionRow $danger={!blocked} onClick={() => toggleBlock().catch((e) => alert((e as Error).message))}>{blocked ? "Unblock" : `Block ${user.firstName}`}</ActionRow>
      </Group>}
    </Sheet>
  );
}

function BlockedRow({ user, onUnblock }: { user: PublicUser; onUnblock?: () => void }) {
  return (
    <Row>
      <Avatar src={user.avatar} />
      <div className="grow"><div className="name">{user.firstName} {user.lastName}</div><div className="sub">{user.username ? `@${user.username} · ` : ""}blocked</div></div>
      {onUnblock && <Button $small onClick={onUnblock}>Unblock</Button>}
    </Row>
  );
}

// ---------------------------------------------------------------- inbox

function InboxRowView({ item: i, me, onOpen, onProfile }: { item: InboxItem; me: Me; onOpen: (i: InboxItem) => void; onProfile: (id: string) => void }) {
  const when = i.day && i.day !== me.today ? i.day : new Date(i.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <Row $tappable onClick={() => (i.kind === "app" ? onOpen(i) : i.fromUser ? onProfile(i.fromUser) : undefined)}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: i.readAt ? "transparent" : "#e5322d", flex: "none" }} />
      <div className="grow"><div style={{ fontWeight: i.readAt ? 500 : 700 }}>{i.title}</div><div className="sub">{i.body}</div></div>
      <Muted>{when}</Muted>
    </Row>
  );
}

// ---------------------------------------------------------------- app

function RetryNotice({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div role="alert" style={{ padding: 12, background: theme.card, borderBottom: `1px solid ${theme.line}`, display: "flex", alignItems: "center", gap: 12 }}>
    <span style={{ flex: 1 }}>{message}</span><Button onClick={onRetry}>Try again</Button>
  </div>;
}

export function App() {
  const { me, config, reload, apply, connectionError } = useMe();
  let content;
  if (me === undefined) content = connectionError ? null : <div style={{ padding: 24 }}><Spinner /> Loading today…</div>;
  else if (!me) content = config ? <Login config={config} onDone={reload} /> : null;
  else if (!me.username) content = <Onboarding me={me} apply={apply} />;
  else content = <Shell me={me} apply={apply} reload={reload} />;
  return <Root>
    {connectionError && <RetryNotice message="Couldn't connect. Your session is still here." onRetry={reload} />}
    <div style={{ flex: 1, minHeight: 0 }}>{content}</div>
  </Root>;
}
